import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Lock, LoaderCircle, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { get, errorMessage } from "@/lib/api";
import { useWorkspaces } from "@/lib/queries";
import { openView } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { useIsUnlocked, lockScope } from "./keyring";
import { UnlockPanel } from "./UnlockPanel";
import { continueProjectEncryption, type EncryptionStatus } from "./convert";
import { LegacyUnlockPanel } from "./LegacyUnlock";

export const encryptionKey = (workspaceId: string) => ["encryption", workspaceId] as const;

export function useWorkspaceEncryption(workspaceId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: encryptionKey(workspaceId ?? ""),
    queryFn: () => get<EncryptionStatus>(`/api/workspaces/${workspaceId}/encryption`),
    enabled: !!workspaceId && enabled,
  });
}

export function useWorkspaceLocked(workspaceId: string | undefined) {
  const workspaces = useWorkspaces();
  const ws = workspaces.data?.find((w) => w.id === workspaceId);
  const unlocked = useIsUnlocked(ws?.encryptionScopeId);
  return { ws, encrypted: !!ws?.encryptionScopeId, locked: (!!ws?.encryptionScopeId && !unlocked) || !!ws?.legacyLocked };
}

/** An end-to-end encrypted project's views do not open until its key is unlocked in this tab. */
export function ProjectGate({ workspaceId, children }: { workspaceId: string | undefined; children: React.ReactNode }) {
  const { ws, encrypted, locked } = useWorkspaceLocked(workspaceId);
  const status = useWorkspaceEncryption(workspaceId, encrypted);
  if (ws?.legacyLocked) return <LegacyUnlockPanel workspace={ws} />;
  if (!encrypted) return <>{children}</>;
  if (locked) {
    const scope = status.data?.scopes.find((s) => s.id === ws!.encryptionScopeId);
    if (!scope) return <p className="p-6 text-muted-foreground" role="status">Loading the project key…</p>;
    const what = ws!.kind === "notebook" ? "notebook" : "project";
    return (
      <UnlockPanel
        scope={scope}
        heading={`This ${what} is end-to-end encrypted`}
        description={`“${ws!.name}” opens only for the people it was shared with. BrainLog and its AI assistant cannot read its notes and files.`}
      />
    );
  }
  return (
    <div className="flex h-full min-h-0 flex-col">
      {status.data && (status.data.pending.notes.length > 0 || status.data.pending.files.length > 0) && <PendingBanner workspaceId={workspaceId!} status={status.data} />}
      <div className="min-h-0 flex-1">{children}</div>
    </div>
  );
}

function PendingBanner({ workspaceId, status }: { workspaceId: string; status: EncryptionStatus }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const n = status.pending.notes.length;
  const f = status.pending.files.length;
  return (
    <div className="flex flex-wrap items-center gap-2 border-b bg-warning/10 px-4 py-2 text-sm" role="status">
      <Lock className="size-4" aria-hidden />
      <span className="flex-1">
        Encryption is not finished: {n} note{n === 1 ? "" : "s"} and {f} file{f === 1 ? "" : "s"} are still stored unencrypted.
        {busy && ` ${progress}`}
      </span>
      <Button
        size="sm"
        variant="outline"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          try {
            await continueProjectEncryption(workspaceId, setProgress);
            toast.success("Everything here is now encrypted.");
          } catch (e) {
            toast.error(errorMessage(e));
          } finally {
            setBusy(false);
            await qc.invalidateQueries();
          }
        }}
      >
        {busy && <LoaderCircle className="animate-spin" />} Encrypt the rest now
      </Button>
    </div>
  );
}

/** Compact sidebar replacement while a project is locked (its settings stay reachable: unlocking and sharing live there). */
export function LockedProjectSidebar({ id, name, kind }: { id: string; name: string; kind?: string }) {
  return (
    <div className="flex w-64 shrink-0 flex-col border-r bg-sidebar text-sm">
      <div className="flex-1 space-y-3 p-4">
        <p className="flex items-center gap-2 font-semibold">
          <Lock className="size-4" aria-hidden /> {name}
        </p>
        <p className="text-muted-foreground">This {kind === "notebook" ? "notebook" : "project"} is end-to-end encrypted and locked. Unlock it in the main area to see its notes, files and records.</p>
      </div>
      <div className="border-t p-2">
        <button type="button" onClick={() => openView("settings", id, "Settings")} className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-accent/70">
          <SlidersHorizontal className="size-4 text-muted-foreground" aria-hidden /> {kind === "notebook" ? "Notebook settings" : "Project settings"}
        </button>
      </div>
    </div>
  );
}

export function LockProjectButton({ scopeId }: { scopeId: string }) {
  return (
    <Button size="sm" variant="ghost" onClick={() => lockScope(scopeId)} className="gap-1">
      <Lock /> Lock
    </Button>
  );
}
